import { call, put, select, takeLeading } from 'typed-redux-saga'
import { errorHandler, showNotificationAction, NOTIFICATION_TYPE, gridSlice } from 'Modules'
import { Deserialize } from 'cerialize'
import { RequestParams, ResponseCatalog } from 'Models'
import {
	DEFAULT_FILTER,
	partnersApi,
	PartnerOneDayShortModel,
	PartnersNS,
	partnersSelector,
	partnersSlice,
} from 'Pages/partner'
import { FieldNames } from 'Constants/FieldNames'

function* getAll(action: PartnersNS.GetAll) {
	try {
		const { params, clear, callback } = action.payload
		const { page, per_page, query, ...fields } = params
		const isFieldStatus = Object.keys(fields).includes(FieldNames.STATUS_SLUG)
		const requestParams = new RequestParams(
			clear ? 1 : page,
			per_page,
			query,
			isFieldStatus ? fields : { ...fields, ...DEFAULT_FILTER }
		)
		yield* put(gridSlice.actions.setLoading(true))
		if (clear) {
			yield* put(partnersSlice.actions.clearAll())
		}
		const res = yield* call(partnersApi.getAll, requestParams)
		const partnersResponse = new ResponseCatalog<PartnerOneDayShortModel>(res)
		partnersResponse.entries = partnersResponse.entries.map(
			(partner): PartnerOneDayShortModel => Deserialize(partner, PartnerOneDayShortModel)
		)
		if (!clear) {
			const oldPartners = yield* select(partnersSelector.getAll)
			partnersResponse.entries = [...oldPartners.entries, ...partnersResponse.entries]
		}
		yield* put(partnersSlice.actions.setAll(partnersResponse))
		yield* put(partnersSlice.actions.setRequestParams(requestParams))
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* getDocuments(action: PartnersNS.GetDocuments) {
	try {
		const res = yield* call(partnersApi.getDocuments, action.payload)
		yield* put(partnersSlice.actions.setDocuments(res))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getOne(action: PartnersNS.GetOne) {
	try {
		const res = yield* call(partnersApi.getOne, action.payload)
		yield* put(partnersSlice.actions.setOne(res))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* setStatus(action: PartnersNS.SetStatus) {
	try {
		const { id, status, callback } = action.payload
		yield* put(partnersSlice.actions.setIsLoading(true))
		yield* call(partnersApi.setStatus, id, status)
		yield* put(
			showNotificationAction({
				type: NOTIFICATION_TYPE.SUCCESS,
				message: 'Статус успешно изменен',
			})
		)
		yield* put(partnersSlice.actions.setIsLoading(false))
		callback?.()
	} catch (error) {
		yield* put(partnersSlice.actions.setIsLoading(false))
		yield* put(
			showNotificationAction({
				type: NOTIFICATION_TYPE.ERROR,
				message: 'Ошибка при смене статуса',
			})
		)
	}
}

function* updateOne(action: PartnersNS.UpdateOne) {
	try {
		const { id, data, callback } = action.payload
		yield* put(partnersSlice.actions.setIsLoading(true))
		const res = yield* call(partnersApi.updateOne, id, data)
		yield* put(partnersSlice.actions.setOne(res))
		yield* put(
			showNotificationAction({
				type: NOTIFICATION_TYPE.SUCCESS,
				message: 'Партнер успешно обновлен',
			})
		)
		yield* put(partnersSlice.actions.setIsLoading(false))
		callback?.()
	} catch (error) {
		yield* put(partnersSlice.actions.setIsLoading(false))
		yield* put(
			showNotificationAction({
				type: NOTIFICATION_TYPE.ERROR,
				message: 'Ошибка при обновлении партнера',
			})
		)
	}
}

function* updateOneDay(action: PartnersNS.UpdateOne) {
	try {
		const { id, data, callback } = action.payload
		yield* put(partnersSlice.actions.setIsLoading(true))
		const res = yield* call(partnersApi.updateOneDay, id, data)
		yield* put(partnersSlice.actions.setOne(res))
		yield* put(
			showNotificationAction({
				type: NOTIFICATION_TYPE.SUCCESS,
				message: 'Партнер успешно обновлен',
			})
		)
		yield* put(partnersSlice.actions.setIsLoading(false))
		yield* put(
			partnersSlice.actions.getAll({
				params: new RequestParams(),
				clear: true,
			})
		)
		callback?.()
	} catch (error) {
		yield* put(partnersSlice.actions.setIsLoading(false))
		yield* put(
			showNotificationAction({
				type: NOTIFICATION_TYPE.ERROR,
				message: 'Ошибка при обновлении партнера',
			})
		)
	}
}

function* postFile(action: PartnersNS.PostFile) {
	try {
		const { file } = action.payload
		yield* put(partnersSlice.actions.setIsLoading(true))
		yield* call(partnersApi.postSvetoforFile, file)
		yield* put(
			showNotificationAction({
				type: NOTIFICATION_TYPE.SUCCESS,
				message: 'Файл успешно загружен',
			})
		)
	} catch (error) {
		yield* put(
			showNotificationAction({
				type: NOTIFICATION_TYPE.ERROR,
				message: 'Ошибка загрузки файла',
			})
		)
	} finally {
		yield* put(partnersSlice.actions.setIsLoading(false))
	}
}

export function* partnersSaga() {
	yield* takeLeading(partnersSlice.actions.getAll.type, getAll)
	yield* takeLeading(partnersSlice.actions.getDocuments.type, getDocuments)
	yield* takeLeading(partnersSlice.actions.getOne.type, getOne)
	yield* takeLeading(partnersSlice.actions.setStatus.type, setStatus)
	yield* takeLeading(partnersSlice.actions.updateOne.type, updateOne)
	yield* takeLeading(partnersSlice.actions.updateOneDay.type, updateOneDay)
	yield* takeLeading(partnersSlice.actions.postFile.type, postFile)
}
